// Helper compartido para importar facturas de servicios públicos (electricidad,
// agua, gas) mediante Claude. Reutilizado por:
//  · MonitorizacionOcupacion (edificio ocupado · tabla building_sensors)
//  · CockpitContratista/Seguimiento (obra · tabla cc_seguimiento_consumos)

import { supabase } from './supabase'

// ── Prompt para extracción de datos de facturas ─────────────────────────────
// Vale para facturas de edificio ocupado y para facturas de obra: el flujo
// caller decide cómo interpretar los resultados. Por defecto fracciona
// facturas multi-mes en N lecturas prorrateadas — se puede desactivar
// pasando { splitByMonth: false } a buildInvoicePrompt.
export function buildInvoicePrompt({ splitByMonth = true } = {}) {
  const reglaSplit = splitByMonth
    ? `Si la factura cubre N meses, divide el consumo y el importe en N partes iguales (consumption/N, amount_eur/N) y devuelve N entradas, una por cada mes calendario del periodo.

Ejemplo: factura agua del 26/11/2024 al 26/02/2025 con 616 m³ y 3.485 € → 3 entradas:
- year 2024, month 12, consumption 205.33, amount_eur 1161.73
- year 2025, month 1,  consumption 205.33, amount_eur 1161.73
- year 2025, month 2,  consumption 205.33, amount_eur 1161.73`
    : `INDEPENDIENTEMENTE del periodo que cubra la factura, devuelve UNA SOLA entrada por factura con el consumo y el importe TOTALES sin prorratear. Como year+month utiliza el mes de la LECTURA FINAL del periodo (o el mes de emisión si no consta lectura). En notes deja el rango de fechas completo del periodo facturado.

Ejemplo: factura agua anual del 01/01/2025 al 31/12/2025 con 616 m³ y 3.485 € → 1 entrada:
- year 2025, month 12, consumption 616, amount_eur 3485, notes "Anual 01/01/2025-31/12/2025"`

  return `Analiza las facturas de servicios públicos adjuntas (electricidad, agua o gas) de un edificio en España.

REGLA CRÍTICA — PERIODO DE FACTURACIÓN:
Identifica el RANGO de fechas que cubre la factura (de la lectura anterior a la lectura actual, NO la fecha de emisión).
- Electricidad: típicamente MENSUAL → 1 lectura.
- Gas: típicamente MENSUAL o BIMESTRAL → 1 o 2 lecturas.
- Agua (especialmente SOREA, Aigües de Catalunya, Aigua de Sant Cugat, Aigues de Barcelona, Hidralia): suele ser TRIMESTRAL → 3 lecturas (una por cada mes del trimestre). También puede ser ANUAL en obras.

${reglaSplit}

Para CADA entrada extrae:
- year (entero): año natural del mes (no necesariamente el año de emisión)
- month (entero 1-12): mes calendario
- kind: "electricity" | "water" | "gas"
- consumption (número): kWh (luz/gas) o m³ (agua) asignados a esa entrada
- amount_eur (número): importe TOTAL A PAGAR (incluye IVA, tasas alcantarillado, residuos, etc. — el grand total que aparece como "TOTAL A PAGAR" o "Total factura"). NO solo el coste del bien.
- supplier (string opcional): nombre del comercializador (SOREA, Endesa, Iberdrola, Naturgy, Aigües de Barcelona, etc.)
- source_filename (string OBLIGATORIO): el nombre EXACTO del archivo PDF/imagen adjunto del que has extraído esta entrada (copia literal del nombre del archivo). Si una factura cubre N meses y se fracciona, las N entradas comparten el mismo source_filename.
- notes (string opcional): periodo original + observaciones

Responde ÚNICAMENTE con JSON válido, sin texto adicional:
{"invoices":[{"year":2025,"month":1,"kind":"water","consumption":205.33,"amount_eur":1161.73,"supplier":"SOREA","source_filename":"factura_sorea_q1.pdf","notes":"Trimestral 26/11/24-26/02/25, prorrateado"}]}`
}

// Alias del prompt por defecto (compatibilidad hacia atrás con importadores
// que aún no pasan opciones).
export const INVOICE_PROMPT = buildInvoicePrompt()

// ── Llamada streaming a la Edge Function breeam-analyzer ─────────────────────
export async function callAnalyzer(file_infos, prompt) {
  const functionUrl = process.env.REACT_APP_SUPABASE_URL?.trim() + '/functions/v1/breeam-analyzer'
  const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY?.trim()
  const response = await fetch(functionUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${anonKey}`, 'apikey': anonKey },
    body: JSON.stringify({ file_infos, prompt_text: prompt, model: 'claude-sonnet-4-6', max_tokens: 8192 }),
  })
  if (!response.ok) {
    let d = {}; try { d = await response.json() } catch {}
    throw new Error(`HTTP ${response.status}${d?.error ? ': ' + d.error : ''}`)
  }
  const ct = response.headers.get('content-type') || ''
  if (ct.includes('text/event-stream')) {
    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let text = '', buf = ''
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buf += decoder.decode(value, { stream: true })
      const lines = buf.split('\n'); buf = lines.pop() || ''
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        const ds = line.slice(6).trim()
        if (!ds || ds === '[DONE]') continue
        try {
          const ev = JSON.parse(ds)
          if (ev.type === 'content_block_delta' && ev.delta?.type === 'text_delta') text += ev.delta.text
          else if (ev.error) throw new Error(ev.error.message || JSON.stringify(ev.error))
        } catch (e) { if (e.message?.startsWith('Error')) throw e }
      }
    }
    return { content: [{ text }] }
  }
  return response.json()
}

// ── Subida temporal de PDFs/imágenes al bucket `evidences` ─────────────────
// Devuelve { file_infos, skipped } donde cada file_info es { name, signed_url,
// media_type, storage_path } listo para pasar a callAnalyzer y para archivar
// después con moveInvoicesToArchive.
export async function uploadTempInvoices(files, user_id, project_id) {
  const file_infos = []
  const skipped = []
  const ts = Date.now()
  for (let i = 0; i < files.length; i++) {
    const f = files[i]
    const nameLow = (f.name || '').toLowerCase()
    const ext = nameLow.split('.').pop()
    const mime = (f.type || '').toLowerCase()
    let media_type = null
    if (mime === 'application/pdf' || ext === 'pdf') media_type = 'application/pdf'
    else if (mime === 'image/jpeg' || ['jpg','jpeg'].includes(ext)) media_type = 'image/jpeg'
    else if (mime === 'image/png' || ext === 'png') media_type = 'image/png'
    if (!media_type) { skipped.push(`${f.name}: extensión "${ext}" / MIME "${mime}" no soportado`); continue }
    const safeName = (f.name || `factura_${i}`).replace(/[^\w.-]+/g, '_')
    const path = `${user_id}/temp_invoices/${project_id}/${ts}_${i}_${safeName}`
    const { error: upErr } = await supabase.storage.from('evidences').upload(path, f, { contentType: media_type, upsert: true })
    if (upErr) { skipped.push(`${f.name}: subida falló — ${upErr.message}`); continue }
    const { data: sd, error: sErr } = await supabase.storage.from('evidences').createSignedUrl(path, 3600)
    if (sErr || !sd?.signedUrl) { skipped.push(`${f.name}: signed URL falló — ${sErr?.message || 'sin URL'}`); continue }
    file_infos.push({ name: f.name, signed_url: sd.signedUrl, media_type, storage_path: path })
  }
  return { file_infos, skipped }
}

// ── Llama al analyzer con el prompt de facturas y devuelve el array parseado
export async function analyzeInvoices(file_infos, promptOverride) {
  const apiData = await callAnalyzer(file_infos, promptOverride || INVOICE_PROMPT)
  const rawText = apiData?.content?.[0]?.text || ''
  const cleaned = rawText.replace(/```(?:json)?\n?/g, '').trim()
  const m = cleaned.match(/\{[\s\S]*\}/)
  if (!m) throw new Error('La IA no devolvió JSON válido')
  const result = JSON.parse(m[0])
  const invoices = (result.invoices || []).filter(i => i.year && i.month && i.kind && i.consumption != null)
  return invoices
}

// ── Elimina los PDFs temporales del bucket evidences (best-effort) ──────────
export async function removeTempInvoices(file_infos) {
  const paths = (file_infos || []).map(f => f.storage_path).filter(Boolean)
  if (!paths.length) return
  try { await supabase.storage.from('evidences').remove(paths) } catch (e) { /* best-effort */ }
}

// ── Mueve los PDFs de /temp_invoices/ al bucket destino y devuelve un mapa
// source_filename → nuevo storage_path (para vincular la evidencia a la fila).
// Si `destBucket` es 'evidences', usamos /invoices_archive/ (patrón del
// componente Ocupación). Si es 'cc-documentos-proyecto', usamos
// /facturas/ (patrón del Cockpit del Contratista).
export async function archiveInvoices(file_infos, user_id, project_id, destBucket = 'evidences') {
  const ts = Date.now()
  const map = new Map()
  const subfolder = destBucket === 'cc-documentos-proyecto' ? 'facturas' : 'invoices_archive'
  for (const fi of file_infos) {
    const safeName = (fi.name || 'factura.pdf').replace(/[^\w.-]+/g, '_')
    const newPath = destBucket === 'evidences'
      ? `${user_id}/${subfolder}/${project_id}/${ts}_${safeName}`
      : `${user_id}/${project_id}/${subfolder}/${ts}_${safeName}`
    if (destBucket === 'evidences') {
      // Mismo bucket → usar move directo
      const { error: mvErr } = await supabase.storage.from('evidences').move(fi.storage_path, newPath)
      if (mvErr) { console.warn(`No se pudo mover ${fi.name}:`, mvErr.message); map.set(fi.name, fi.storage_path); continue }
      map.set(fi.name, newPath)
    } else {
      // Cross-bucket → descargar + subir + borrar original
      const { data: blob, error: dlErr } = await supabase.storage.from('evidences').download(fi.storage_path)
      if (dlErr || !blob) { console.warn(`No se pudo descargar ${fi.name}:`, dlErr?.message); map.set(fi.name, fi.storage_path); continue }
      const { error: upErr } = await supabase.storage.from(destBucket).upload(newPath, blob, { contentType: fi.media_type, upsert: true })
      if (upErr) { console.warn(`No se pudo subir ${fi.name} a ${destBucket}:`, upErr.message); map.set(fi.name, fi.storage_path); continue }
      await supabase.storage.from('evidences').remove([fi.storage_path])
      map.set(fi.name, newPath)
    }
  }
  return map
}
